// Stock-level buy signals only; no topic, position or exit state enters this module.
export const SIGNAL_EPSILON = 1e-10

export function movingAverage(values, period) {
    return values.map((value, index) => {
        if (index < period - 1) return null
        const window = values.slice(index - period + 1, index + 1)
        return window.every(Number.isFinite) ? window.reduce((sum, item) => sum + item, 0) / period : null
    })
}

export function marketClock(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(now)
    const part = type => parts.find(item => item.type === type).value
    return { date: part('year') + part('month') + part('day'), closed: Number(part('hour')) * 60 + Number(part('minute')) >= 905 }
}

const number = value => value === null || value === undefined || value === '' || value === '-' ? NaN : Number(value)
const above = (a, b) => Number.isFinite(a) && Number.isFinite(b) && b > 0 && a / b > 1 + SIGNAL_EPSILON
const below = (a, b) => Number.isFinite(a) && Number.isFinite(b) && b > 0 && a / b < 1 - SIGNAL_EPSILON

export function calculateBuySignals(data, now = new Date()) {
    const count = data.values.length
    const rawClose = data.values.map(row => number(row[1]))
    const rawOpen = data.values.map(row => number(row[0]))
    const rawLow = data.values.map(row => number(row[2]))
    const volume = data.vols.map(row => number(row[1]))
    const factors = []
    // Causal previous-close reference chain, same approximation as research.
    for (let i = 0; i < count; i++) {
        const reference = number(data.values_preclose && data.values_preclose[i])
        factors.push(i === 0 ? 1 : reference > 0 && rawClose[i - 1] > 0
            ? factors[i - 1] * rawClose[i - 1] / reference : NaN)
    }
    const close = rawClose.map((value, i) => value * factors[i])
    const low = rawLow.map((value, i) => value * factors[i])
    const ma5 = movingAverage(close, 5)
    const ma25 = movingAverage(close, 25)
    const vol5 = movingAverage(volume, 5)
    const vol60 = movingAverage(volume, 60)
    const clock = marketClock(now)
    const valid = close.map((value, i) => {
        const date = String(data.categoryData[i] || '').replace(/-/g, '')
        const previous = i > 0 ? String(data.categoryData[i - 1] || '').replace(/-/g, '') : ''
        return i >= 73 && /^[0-9]{8}$/.test(date) && (i === 0 || date > previous) &&
            (date < clock.date || (date === clock.date && clock.closed)) &&
            value > 0 && rawOpen[i] > 0 && rawLow[i] > 0 && factors[i] > 0 && volume[i] > 0 &&
            Number.isFinite(ma25[i]) && Number.isFinite(ma25[i - 1]) && Number.isFinite(vol60[i]) &&
            !(data.is_st && number(data.is_st[i]) === 1) &&
            !(data.is_paused && number(data.is_paused[i]) === 1)
    })
    const patterns = { surge: [], golden: [], resonance: [], B1: [], B2: [], B3: [] }
    const all = (start, end, predicate) => {
        for (let i = start; i <= end; i++) if (i < 0 || !predicate(i)) return false
        return true
    }
    for (let i = 0; i < count; i++) {
        const trend = above(ma25[i], ma25[i - 1])
        const volumeAbove = above(vol5[i], vol60[i])
        const gate = valid[i] && trend && volumeAbove
        const crossVolume = volumeAbove && Number.isFinite(vol60[i - 1]) &&
            vol60[i - 1] > 0 && vol5[i - 1] / vol60[i - 1] <= 1 + SIGNAL_EPSILON
        const distance = close[i] / ma25[i] - 1
        patterns.surge.push(Boolean(gate && crossVolume && low[i] <= ma25[i] * 1.01 && distance >= -.01 && distance <= .03))
        patterns.golden.push(Boolean(gate && above(ma5[i], ma25[i]) && ma5[i - 1] / ma25[i - 1] <= 1 + SIGNAL_EPSILON))
        patterns.resonance.push(Boolean(gate && above(close[i], ma25[i])))
        const earlyVolumeCount = volume.slice(i - 13, i - 3).reduce((sum, _, offset) =>
            sum + (below(vol5[i - 13 + offset], vol60[i - 13 + offset]) ? 1 : 0), 0)
        patterns.B1.push(Boolean(gate && all(i - 2, i, j => above(close[j], ma25[j]) && above(vol5[j], vol60[j])) && earlyVolumeCount >= 7))

        let exceptions = 0
        const priceContracting = all(i - 5, i - 2, j => {
            if (![ma5[j], ma5[j - 1], ma25[j]].every(Number.isFinite)) return false
            if (ma5[j] > ma5[j - 1] || ma5[j] < ma25[j]) {
                const increase = (ma5[j] / ma5[j - 1] - 1) * 100
                if (!(increase > 0 && increase < 10)) return false
                exceptions += 1
            }
            return exceptions <= 1
        })
        const volumeContracting = all(i - 5, i - 2, j =>
            Number.isFinite(vol5[j]) && Number.isFinite(vol60[j]) && vol60[j] > 0 &&
            vol5[j] <= vol5[j - 1] && vol5[j] / vol60[j] >= .92)
        // Preserve the original actual 2% threshold; remove misleading 5% comments.
        patterns.B2.push(Boolean(gate && priceContracting && volumeContracting &&
            volume[i] > volume[i - 1] * 1.3 && rawClose[i - 1] > 0 && rawClose[i] / rawClose[i - 1] > 1.02))

        const priorMeans = movingAverage(volume.slice(Math.max(0, i - 15), Math.max(0, i - 3)), 3).filter(Number.isFinite)
        const minimum = priorMeans.length === 10 ? Math.min(...priorMeans) : NaN
        // Yesterday's close, volume and volume MAs refer to the same three bars.
        patterns.B3.push(Boolean(gate && all(i - 12, i - 2, j => above(close[j], ma25[j])) &&
            below(close[i - 1], ma25[i - 1]) && above(close[i], ma25[i]) &&
            all(i - 3, i - 1, j => volume[j] < minimum && below(vol5[j], vol60[j]))))
    }
    // State transitions are calculated on all stock bars, independent of viewport.
    const signals = {}
    Object.keys(patterns).forEach(key => {
        signals[key] = patterns[key].map((active, i) => active && (i === 0 || !patterns[key][i - 1]))
    })
    return { signals, patterns, ma5, ma25, vol5, vol60, factors }
}
